import { useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { FiFacebook, FiYoutube, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useSiteData } from '../../context/SiteDataContext';
import config from '../../config';
import './Footer.css';

const Footer = memo(() => {
  const { settings, categories } = useSiteData();

  useEffect(() => {
    if (settings.logo_url) {
      document.documentElement.style.setProperty('--footer-watermark', `url(${settings.logo_url})`);
    }
  }, [settings.logo_url]);

  const currentYear = new Date().getFullYear();

  return (
    <>
      <footer className="footer">
        <Container>
          {/* Top Section - Logo & Contact */}
          <div className="footer-top">
            <Row className="align-items-center">
              <Col lg={4} md={6} className="mb-3 mb-lg-0">
                <Link to="/" className="footer-logo">
                  <img 
                    src={settings.logo_url || '/images/logo.png'} 
                    alt={config.site.name} 
                    width="135"
                    height="45"
                  />
                </Link>
                <p className="footer-tagline">{config.site.tagline}</p>
              </Col>
              <Col lg={8} md={6}>
                <div className="footer-contact-grid">
                  <div className="footer-contact-item">
                    <FiPhone className="contact-icon" />
                    <div>
                      <span className="contact-label">Phone</span>
                      <a href={`tel:${config.contact.primaryPhone}`} className="contact-value">{config.contact.primaryPhone}</a>
                      <a href={`tel:${config.contact.secondaryPhone}`} className="contact-value secondary-phone">{config.contact.secondaryPhone}</a>
                    </div>
                  </div>
                  <div className="footer-contact-item">
                    <FiMail className="contact-icon" />
                    <div>
                      <span className="contact-label">Email</span>
                      <a href={`mailto:${config.contact.supportEmail}`} className="contact-value">{config.contact.supportEmail}</a>
                    </div>
                  </div>
                  <div className="footer-contact-item">
                    <FiMapPin className="contact-icon" />
                    <div>
                      <span className="contact-label">{config.offices.corporate.label} (Product Showroom)</span>
                      <span className="contact-value address-text">{config.offices.corporate.address}</span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Main Footer Content */}
          <div className="footer-main">
            <Row>
              {/* About Us */}
              <Col lg={3} md={6} sm={6}>
                <div className="footer-widget">
                  <h5 className="widget-title">ABOUT US</h5>
                  <ul className="footer-links">
                    <li><Link to="/about">About Us</Link></li>
                    <li><Link to="/contact">Contact Us</Link></li>
                    <li><Link to="/about">Our Services</Link></li>
                  </ul>
                </div>
              </Col>

              {/* Policy */}
              <Col lg={3} md={6} sm={6}>
                <div className="footer-widget">
                  <h5 className="widget-title">POLICY</h5>
                  <ul className="footer-links">
                    <li><Link to="/warranty-policy">Warranty Policy</Link></li>
                    <li><Link to="/return-policy">Return & Refund Policy</Link></li>
                    <li><Link to="/terms">Terms & Conditions</Link></li>
                  </ul>
                </div>
              </Col>

              {/* Categories */}
              <Col lg={3} md={6} sm={6}>
                <div className="footer-widget">
                  <h5 className="widget-title">CATEGORIES</h5>
                  <ul className="footer-links">
                    {categories.slice(0, 5).map((cat) => (
                      <li key={cat.id}>
                        <Link to={`/categories/${cat.slug}`}>{cat.name}</Link>
                      </li>
                    ))}
                    <li><Link to="/products">View All</Link></li>
                  </ul>
                </div>
              </Col>

              {/* Office Addresses */}
              <Col lg={3} md={6} sm={6}>
                <div className="footer-widget">
                  <h5 className="widget-title">OUR OFFICES</h5>
                  <div className="footer-office">
                    <strong>{config.offices.head.label}</strong>
                    <p>{config.offices.head.address}</p>
                  </div>
                  <div className="footer-office">
                    <strong>{config.offices.corporate.label}</strong>
                    <p>{config.offices.corporate.address}</p>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Footer Bottom */}
          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p className="copyright">© {currentYear} {config.site.name} | All rights reserved | Since {config.site.foundedYear}</p>
              <div className="social-links">
                <a href={config.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <FiFacebook />
                </a>
                <a href={config.social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <FiYoutube />
                </a>
                {config.contact.whatsapp && (
                  <a href={`https://wa.me/${config.contact.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                    <FaWhatsapp />
                  </a>
                )}
              </div>
            </div>
          </div>
        </Container>
      </footer>

      {/* WhatsApp Floating Button */}
      {config.contact.whatsapp && (
        <a
          href={`https://wa.me/${config.contact.whatsapp.replace(/[^0-9]/g, '')}?text=Hello! I'm interested in your products.`}
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float"
          aria-label="Chat on WhatsApp"
        >
          <FaWhatsapp />
        </a>
      )}
    </>
  );
});

export default Footer;
